import { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { MapPin, Sparkles, ArrowRight, Wind, Droplet, ShieldCheck } from "lucide-react";
import { CITY_META, FEATURED_CITY_SLUGS } from "@/config/cities";

/** Auto-generated fallback when a city has properties but isn't in CITY_META. */
function autoMeta(cityName, countryName) {
  return {
    slug: cityName.toLowerCase().replace(/\s+/g, "-"),
    name: cityName,
    city: cityName,
    country: countryName,
    region: countryName,
    hero: "https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=1600",
    tagline: `Certified fresh stays in ${cityName}.`,
    lede: `Every Guest Smell Certified™ property in ${cityName}, ${countryName} has passed the same air quality, humidity, and freshness audit. Browse verified stays below.`,
    facts: [],
    keywords: `certified stays ${cityName}, clean Airbnb ${cityName}, verified freshness ${cityName}`,
  };
}

/** Lightweight SEO meta injector — sets title + description + OG tags on mount. */
function useSEO({ title, description, keywords, image, url }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };
    const setCanonical = (href) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return el;
    };

    const desc = setMeta("description", description);
    const kw = setMeta("keywords", keywords);
    const ogT = setMeta("og:title", title, true);
    const ogD = setMeta("og:description", description, true);
    const ogI = setMeta("og:image", image, true);
    const ogU = setMeta("og:url", url, true);
    const ogType = setMeta("og:type", "website", true);
    const twC = setMeta("twitter:card", "summary_large_image");
    const canon = setCanonical(url);

    return () => {
      document.title = prevTitle;
      // Leave meta tags in place — cheaper than teardown and Google likes stability.
    };
  }, [title, description, keywords, image, url]);
}

export default function CityPage() {
  const { slug } = useParams();
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/properties").then((r) => { setAllProperties(r.data); setLoading(false); });
  }, []);

  const meta = CITY_META[slug];

  // If we don't have curated meta, try to infer from a matching property.
  const inferredMeta = useMemo(() => {
    if (meta) return null;
    const wantsCity = slug ? slug.replace(/-/g, " ") : "";
    const match = allProperties.find(
      (p) => (p.city || "").toLowerCase() === wantsCity.toLowerCase()
    );
    return match ? autoMeta(match.city, match.country) : null;
  }, [meta, slug, allProperties]);

  const activeMeta = meta || inferredMeta;

  const cityProperties = useMemo(() => {
    if (!activeMeta) return [];
    return allProperties.filter(
      (p) => (p.city || "").toLowerCase() === activeMeta.city.toLowerCase()
    );
  }, [allProperties, activeMeta]);

  const avgScore = useMemo(() => {
    if (cityProperties.length === 0) return null;
    const scores = cityProperties.map((p) => p.freshness_score).filter((s) => typeof s === "number");
    if (!scores.length) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [cityProperties]);

  const seoUrl = `https://guestsmell.com/stays/${slug}`;
  useSEO({
    title: activeMeta ? `${activeMeta.name} · Guest Smell Certified™ Stays` : "Certified Stays · Guest Smell",
    description: activeMeta ? activeMeta.lede : "Certified fresh short-term stays verified by Guest Smell.",
    keywords: activeMeta ? activeMeta.keywords : "certified stays, verified freshness",
    image: activeMeta ? activeMeta.hero : "https://guestsmell.com/og-default.jpg",
    url: seoUrl,
  });

  if (loading) {
    return <div className="max-w-6xl mx-auto p-16 text-slate-500">Loading…</div>;
  }
  if (!activeMeta) {
    // Slug doesn't map to any city with properties → send them to /browse
    return <Navigate to="/browse" replace />;
  }

  return (
    <div className="scent-wave" data-testid="city-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs tracking-[0.18em] uppercase font-semibold">
              <MapPin className="w-3 h-3" />
              {activeMeta.region ? `${activeMeta.region} · ${activeMeta.country}` : activeMeta.country}
            </div>
            <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#0A1128]" data-testid="city-heading">
              Certified stays in <span className="italic text-emerald-600">{activeMeta.name}</span>.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-lg">
              {activeMeta.tagline}
            </p>
            <p className="mt-4 text-slate-500 leading-relaxed max-w-lg">
              {activeMeta.lede}
            </p>

            {/* Stats */}
            <div className="mt-10 flex items-center gap-8 text-sm text-slate-500">
              <div>
                <span className="font-serif text-3xl text-[#0A1128]">{cityProperties.length}</span>
                <div className="text-xs uppercase tracking-[0.2em] mt-1">certified stay{cityProperties.length === 1 ? "" : "s"}</div>
              </div>
              {avgScore != null && (
                <>
                  <div className="w-px h-10 bg-slate-300" />
                  <div>
                    <span className="font-serif text-3xl text-[#0A1128]">{avgScore}</span>
                    <div className="text-xs uppercase tracking-[0.2em] mt-1">avg freshness score</div>
                  </div>
                </>
              )}
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/browse" data-testid="city-browse-cta">
                <Button size="lg" className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white px-8">
                  Browse all certified stays <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/register" data-testid="city-host-cta">
                <Button size="lg" variant="outline" className="rounded-full border-[#D4AF37] text-[#0A1128] hover:bg-[#F9F3E5] px-8">
                  Host in {activeMeta.name}? Certify →
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={activeMeta.hero} alt={`Certified stays in ${activeMeta.name}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/50 via-transparent to-transparent" />
            </div>
            {activeMeta.facts.length > 0 && (
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-slate-200 hidden md:block max-w-[240px]">
                <div className="text-[10px] tracking-[0.24em] uppercase text-emerald-700 font-semibold">Local facts</div>
                <ul className="mt-3 space-y-2 text-xs text-slate-600">
                  {activeMeta.facts.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* THE 3-POINT LOCAL STANDARD */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Wind, title: "Air Quality", desc: `Every certified ${activeMeta.name} property monitors PM2.5 and CO₂ in real time.` },
            { icon: Droplet, title: "Humidity 30-55%", desc: `The hospitality humidity sweet-spot, verified 24/7.` },
            { icon: ShieldCheck, title: "Turnover Verified", desc: `Time-stamped cleaning between every guest — no exceptions.` },
          ].map((f) => (
            <div key={f.title} className="bg-white rounded-3xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-xl transition-transform duration-200">
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <f.icon className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="mt-5 font-serif text-xl text-[#0A1128]">{f.title}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROPERTY GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24" data-testid="city-property-grid">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Verified Listings</div>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0A1128] tracking-tight">
              {cityProperties.length} certified stay{cityProperties.length === 1 ? "" : "s"} in {activeMeta.name}
            </h2>
          </div>
          <Sparkles className="w-6 h-6 text-[#D4AF37]" />
        </div>

        {cityProperties.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
            No certified stays here yet. <Link to="/register" className="text-emerald-700 underline">Be the first — certify your {activeMeta.name} property.</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cityProperties.map((p) => (
              <PropertyCard key={p.id} p={p} />
            ))}
          </div>
        )}
      </section>

      {/* NEARBY / OTHER FEATURED CITIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Also verified</div>
        <h2 className="mt-2 font-serif text-2xl md:text-3xl text-[#0A1128] tracking-tight">Certified stays in other cities</h2>
        <div className="mt-6 flex flex-wrap gap-2">
          {FEATURED_CITY_SLUGS.filter((s) => s !== slug).map((s) => (
            <Link key={s} to={`/stays/${s}`} data-testid={`city-crosslink-${s}`}>
              <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 text-slate-700 text-sm transition-colors">
                {CITY_META[s]?.name || s}
                <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
